import { useEffect, type JSX } from "react";
import { Navigate, useLocation } from "react-router";
import { useGetMeQuery } from "@/features/auth/api";
import { setAuth, setSessionCheckDone } from "@/features/auth/slice";
import { Loader } from "@/shared/components";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { HOME_PAGE_ROUTE, CHANGE_PASSWORD_PAGE_ROUTE, FORGOT_PASSWORD_PAGE_ROUTE, RESET_PASSWORD_PAGE_ROUTE } from "@/shared/utils";

interface PublicRouteProps {
  children: JSX.Element;
  alwaysAccessible?: boolean;
}

export default function PublicRoute({ children, alwaysAccessible }: PublicRouteProps) {
  const dispatch = useAppDispatch();
  const isAuth = useAppSelector((state) => state.auth.isAuth);
  const mustChangePassword = useAppSelector((state) => state.auth.mustChangePassword);
  const sessionCheckDone = useAppSelector((state) => state.auth.sessionCheckDone);
  const location = useLocation();

  const shouldSkip = isAuth || sessionCheckDone;

  const { data, isLoading, isSuccess } = useGetMeQuery(undefined, {
    skip: shouldSkip,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (!shouldSkip && !isLoading) {
      dispatch(setSessionCheckDone(true));
    }
  }, [shouldSkip, isLoading, dispatch]);

  useEffect(() => {
    if (!shouldSkip && !isLoading && isSuccess && data) {
      dispatch(setAuth(data));
    }
  }, [shouldSkip, isLoading, isSuccess, data, dispatch]);

  if (!shouldSkip && isLoading) {
    return <Loader isFullPage />;
  }

  if (alwaysAccessible) {
    return children;
  }

  if (isAuth && !mustChangePassword) {
    if (location.pathname === FORGOT_PASSWORD_PAGE_ROUTE || location.pathname === RESET_PASSWORD_PAGE_ROUTE) {
      return children;
    }
    return <Navigate to={HOME_PAGE_ROUTE} replace />;
  }

  if (mustChangePassword && location.pathname === CHANGE_PASSWORD_PAGE_ROUTE) {
    return children;
  }

  if (location.pathname === FORGOT_PASSWORD_PAGE_ROUTE || location.pathname === RESET_PASSWORD_PAGE_ROUTE) {
    return children;
  }

  return children;
}
